import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight,
  Sparkles, ShieldCheck, Wrench, Bug, UtensilsCrossed, Leaf, Users, Building2, Plane,
  HeartPulse, Factory, ShoppingBag, GraduationCap, Utensils, Landmark, Home as HomeIcon,
  Award, Cpu, Headphones as HeadphonesIcon, CheckCircle2, Quote, MapPin, Phone, Star,
  BadgeCheck, MessageCircle,
} from 'lucide-react';
import {
  brand, heroSlides, stats, states, services, industries, whyChooseUs,
  approachSteps, certifications, testimonials, clientLogos, contactInfo,
} from '../data/mock';
import FAQ from '../components/FAQ';

const iconMap = {
  Sparkles, ShieldCheck, Wrench, Bug, UtensilsCrossed, Leaf, Users, Building2, Plane,
  HeartPulse, Factory, ShoppingBag, GraduationCap, Utensils, Landmark, Home: HomeIcon,
  Award, Cpu, HeadphonesIcon,
};

// -------- HERO --------
const rotatingStats = [
  { icon: 'Users', label: 'Workforce', value: '1511+', sub: 'Trained & background-verified professionals' },
  { icon: 'Award', label: 'Years of Experience', value: '19+', sub: 'Delivering desired results — heritage since 1994' },
  { icon: 'MapPin', label: 'States of Operation', value: '5', sub: 'Karnataka · Goa · Maharashtra · Tamil Nadu · Telangana' },
  { icon: 'Building2', label: 'Corporate Clients', value: '103+', sub: 'Across IT, healthcare, hospitality & more' },
];

function Hero() {
  const [idx, setIdx] = useState(0);
  const [statIdx, setStatIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % heroSlides.length), 7000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const t = setInterval(() => setStatIdx((i) => (i + 1) % rotatingStats.length), 3200);
    return () => clearInterval(t);
  }, []);
  const slide = heroSlides[idx];
  const rs = rotatingStats[statIdx];
  const RSIcon = iconMap[rs.icon] || Users;

  return (
    <section className="relative bg-white overflow-hidden">
      {/* Subtle grid backdrop */}
      <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" style={{ maskImage: 'radial-gradient(ellipse at top, black 30%, transparent 70%)', WebkitMaskImage: 'radial-gradient(ellipse at top, black 30%, transparent 70%)' }} />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-8 pt-14 pb-24 lg:pt-20 lg:pb-32 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
        <div>
          <div className="inline-flex items-center gap-2 bg-[#F5B800]/15 text-[#0A2540] px-3 py-1.5 rounded-full text-xs font-semibold border border-[#F5B800]/30">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F5B800]" />
            {slide.kicker}
          </div>

          <div className="mt-6 text-[11px] uppercase tracking-[0.28em] text-slate-500 font-semibold">
            Durga Facility Management Services Pvt. Ltd.
          </div>

          <h1 key={idx} className="mt-3 text-slate-900 text-[40px] sm:text-5xl lg:text-[64px] xl:text-[72px] font-extrabold leading-[1.02] tracking-tight animate-fadeUp">
            {slide.title}{' '}
            <span className="text-[#0A2540] relative inline-block">
              {slide.highlight}
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#F5B800]/40 -z-10 rounded-sm" aria-hidden="true" />
            </span>
          </h1>

          <p className="mt-6 text-slate-600 text-base md:text-lg leading-relaxed max-w-xl animate-fadeUp" style={{ animationDelay: '0.1s' }}>
            {slide.subtitle}
          </p>

          <div className="mt-9 flex flex-wrap gap-3 animate-fadeUp" style={{ animationDelay: '0.2s' }}>
            <Link
              to="/contact"
              className="btn-cta inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-[15px]"
            >
              Request a Quote <ArrowRight size={16} />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-slate-900 hover:text-[#0A2540] px-7 py-3.5 rounded-xl font-semibold text-[15px] border border-slate-200 hover:border-slate-300 bg-white transition-colors"
            >
              Explore Services <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="mt-10 pt-8 border-t border-slate-100 flex flex-wrap items-center gap-x-6 gap-y-3 max-w-lg">
            {['India 500 Best MSME 2024', 'ISO 9001 · 14001 · 45001', 'PSARA · FSSAI Licensed'].map((t) => (
              <div key={t} className="flex items-center gap-2 text-xs text-slate-600 font-medium">
                <BadgeCheck size={14} className="text-[#F5B800]" /> {t}
              </div>
            ))}
          </div>
        </div>

        {/* Right visual */}
        <div className="relative">
          <div className="absolute -top-3 left-3 right-3 flex gap-2 z-20">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`h-1 flex-1 rounded-full transition-all ${i === idx ? 'bg-[#F5B800]' : 'bg-slate-200 hover:bg-slate-300'}`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>

          <div className="relative rounded-3xl overflow-hidden aspect-[4/5] lg:aspect-[5/6] shadow-xl bg-slate-100 border border-slate-100">
            {heroSlides.map((s, i) => (
              <img
                key={s.id}
                src={s.image}
                alt=""
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === idx ? 'opacity-100' : 'opacity-0'}`}
                style={{ transform: i === idx ? 'scale(1.05)' : 'scale(1)', transition: 'transform 8s ease-out, opacity 1s' }}
              />
            ))}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />

            <div className="absolute top-5 left-5 bg-white/95 backdrop-blur rounded-xl p-3 shadow-md flex items-center gap-2.5 max-w-[240px] border border-white/50">
              <div className="w-10 h-10 rounded-lg bg-[#F5B800] text-[#0A2540] flex items-center justify-center shrink-0">
                <Award size={20} />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#0A2540]">Awarded</div>
                <div className="text-xs font-semibold text-slate-900 leading-tight mt-0.5">India 500 Best MSME 2024</div>
              </div>
            </div>
          </div>

          {/* Rotating stat card (workforce → years → states → clients) */}
          <div className="mt-6 md:mt-0 md:absolute md:-bottom-8 md:-left-8 bg-white rounded-2xl shadow-lg border border-slate-100 p-5 md:w-64 overflow-hidden">
            <div key={statIdx} className="animate-fadeUp">
              <div className="flex items-center gap-2 text-[#0A2540] text-xs font-bold uppercase tracking-widest">
                <RSIcon size={14} /> {rs.label}
              </div>
              <div className="text-3xl font-extrabold text-slate-900 mt-2 tracking-tight">{rs.value}</div>
              <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">{rs.sub}</div>
            </div>
            <div className="mt-3 flex gap-1">
              {rotatingStats.map((_, i) => (
                <span
                  key={i}
                  className={`h-1 flex-1 rounded-full transition-all ${i === statIdx ? 'bg-[#F5B800]' : 'bg-slate-200'}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// -------- STATS STRIP --------
function StatsStrip() {
  return (
    <section className="bg-slate-50 border-y border-slate-100">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
        {stats.map((s, i) => (
          <div key={i} className={`text-center ${i < stats.length - 1 ? 'md:border-r md:border-slate-200' : ''}`}>
            <div className="text-4xl md:text-5xl font-extrabold text-[#0A2540] tracking-tight">{s.num}</div>
            <div className="text-xs md:text-sm text-slate-500 mt-1.5 uppercase tracking-wider">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// -------- ABOUT SNIPPET --------
function AboutSnippet() {
  return (
    <section className="py-16 md:py-36 bg-white">
      <div className="max-w-[1300px] mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <div className="relative">
          <div className="grid grid-cols-5 grid-rows-6 gap-3 h-[380px] md:h-[520px]">
            <div className="col-span-3 row-span-4 rounded-2xl overflow-hidden shadow-md">
              <img src="/assets/mechanised-housekeeping.jpg" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-2 row-span-3 rounded-2xl overflow-hidden shadow-md">
              <img src="https://images.pexels.com/photos/6949934/pexels-photo-6949934.jpeg?auto=compress&cs=tinysrgb&w=800" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-2 row-span-3 rounded-2xl overflow-hidden shadow-md">
              <img src="/assets/hk-lobby.webp" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-3 row-span-2 rounded-2xl overflow-hidden shadow-md">
              <img src="https://images.unsplash.com/photo-1529939607658-044a84095c6f?auto=format&fit=crop&w=1200&q=80" alt="" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>

        <div>
          <div className="text-[#0A2540] uppercase text-xs font-bold tracking-[0.25em] mb-4">About Durga</div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.05] tracking-tight mb-6">
            Three decades of trust, delivered with discipline.
          </h2>
          <p className="text-slate-600 leading-relaxed text-[15px] mb-4">
            Durga&rsquo;s story began in <b>1994</b> as a single canteen operation in Mangalore. Over the next three decades we grew into a full-spectrum integrated facility management company &mdash; formally incorporated as <b>Durga Facility Management Services Pvt. Ltd.</b> in 2007. Today we run housekeeping, security, technical maintenance, pest control, F&amp;B, horticulture and drone fa&ccedil;ade cleaning for <b>103+ organisations across five states</b>.
          </p>
          <p className="text-slate-600 leading-relaxed text-[15px] mb-8">
            Every engagement carries three decades of on-ground learning &mdash; condensed into SOPs, safety systems and a workforce that shows up ready. The promise we began with still holds: measurable outcomes, ethical practice and quiet excellence.
          </p>

          <div className="grid grid-cols-2 gap-3 mb-8">
            {['30+ years of on-ground heritage', 'ISO 9001 / 14001 / 45001 aligned', 'PSARA & FSSAI licensed', 'India 500 Best MSME 2024'].map((t) => (
              <div key={t} className="flex items-center gap-2.5 text-sm text-slate-700">
                <BadgeCheck size={16} className="text-[#0A2540] shrink-0" /> {t}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-3">
            <Link to="/about" className="btn-cta inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-[14px]">
              Read our story <ArrowRight size={16} />
            </Link>
            <Link to="/why-durga" className="inline-flex items-center gap-2 text-slate-800 hover:text-[#0A2540] px-6 py-3 rounded-lg font-semibold text-[14px] transition-colors border border-slate-200 hover:border-slate-300">
              Why Durga <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// -------- SERVICES GRID --------
function ServicesGrid() {
  return (
    <section id="services" className="py-16 md:py-36 bg-slate-50 border-y border-slate-100">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="text-[#0A2540] uppercase text-xs font-bold tracking-[0.25em] mb-4">What we do</div>
            <h2 className="text-3xl md:text-4xl lg:text-[46px] font-bold text-slate-900 leading-[1.05] tracking-tight">
              Integrated services under a single accountable partner.
            </h2>
            <p className="text-slate-600 mt-4 text-[15px] leading-relaxed">
              From industrial cleaning to India&rsquo;s newest addition &mdash; drone facade cleaning &mdash; choose a single service or bundle everything under one SLA.
            </p>
          </div>
          <Link to="/services" className="inline-flex items-center gap-2 text-[#0A2540] hover:text-[#1E40AF] font-semibold text-sm group">
            View all services <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s) => {
            const Icon = iconMap[s.icon] || Building2;
            return (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="group relative bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-slate-200 hover:shadow-xl transition-all duration-500 hover:-translate-y-1"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-slate-100" style={s.imgBg ? { background: s.imgBg } : undefined}>
                  <img
                    src={s.image}
                    alt={s.title}
                    style={{ objectPosition: s.imgPos || 'center', objectFit: s.imgFit || 'contain' }}
                    className="w-full h-full group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-slate-900/10 to-transparent" />
                  <div className="absolute top-4 left-4 w-11 h-11 rounded-xl bg-white/95 backdrop-blur flex items-center justify-center text-[#0A2540] shadow-md">
                    <Icon size={20} strokeWidth={2} />
                  </div>
                  {s.isNew && (
                    <span className="absolute top-4 right-4 text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#F5B800] text-[#0A2540] uppercase tracking-widest">New</span>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-lg md:text-xl font-bold text-slate-900 group-hover:text-[#0A2540] transition-colors tracking-tight">{s.title}</h3>
                  <p className="text-sm text-slate-600 mt-2 line-clamp-2 leading-relaxed">{s.short}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {s.highlights.slice(0, 2).map((h) => (
                      <span key={h} className="text-[11px] font-medium px-2 py-1 rounded-md bg-slate-100 text-slate-600">{h}</span>
                    ))}
                  </div>
                  <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A2540]">
                    Learn more <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// -------- DRONE VIDEO STRIP --------
function DroneVideoStrip() {
  return (
    <section className="py-14 md:py-28 bg-[#0A2540] text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(circle at 30% 20%, #fff 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
      <div className="max-w-[1300px] mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-12 items-center relative">
        <div>
          <div className="inline-flex items-center gap-2 bg-[#F5B800]/20 text-[#F5B800] px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest mb-5 border border-[#F5B800]/30">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F5B800] animate-pulse" /> Watch · Drone in action
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-[46px] font-bold leading-[1.05] tracking-tight">
            Drone facade cleaning, delivered by Durga.
          </h2>
          <p className="text-white/70 mt-5 text-[15px] leading-relaxed max-w-lg">
            India-ready drone cleaning for high-rise glass &mdash; safer than rope access, faster than BMU, and up to 60% more water-efficient. See our team operate an autonomous cleaning drone at Sri Durgaparameshwari Temple, Kateel.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/services/drone-facade-cleaning" className="btn-cta inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-[14px]">
              Explore drone cleaning <ArrowRight size={16} />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 text-white/90 hover:text-white px-6 py-3 rounded-lg font-semibold text-[14px] transition-colors border border-white/20 hover:border-white/40">
              Get a quote <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>

        <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video bg-black">
          <video
            src="/assets/drone-cleaning.mp4"
            className="w-full h-full object-cover"
            controls
            playsInline
            preload="metadata"
            poster="/assets/drone-facade-cleaning.jpg"
          />
        </div>
      </div>
    </section>
  );
}


// -------- WHY DURGA (light) --------
function WhyDurgaHome() {
  return (
    <section className="py-16 md:py-36 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-12 items-start mb-14">
          <div>
            <div className="text-[#0A2540] uppercase text-xs font-bold tracking-[0.25em] mb-4">Why choose Durga</div>
            <h2 className="text-3xl md:text-4xl lg:text-[46px] font-bold text-slate-900 leading-[1.05] tracking-tight">
              Built for organisations that can&rsquo;t compromise on outcomes.
            </h2>
          </div>
          <p className="text-slate-600 leading-relaxed text-[15px] lg:pt-6">
            Every Durga engagement runs on three pillars &mdash; trained people, modern equipment and disciplined processes. It&rsquo;s why organisations in IT, healthcare, education, manufacturing and hospitality trust us with facilities that never sleep.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {whyChooseUs.map((w) => {
            const Icon = iconMap[w.icon] || Award;
            return (
              <div key={w.title} className="group p-7 rounded-2xl bg-white border border-slate-100 hover:border-slate-200 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-[#0A2540] mb-5 group-hover:bg-[#F5B800] group-hover:border-[#F5B800] transition-colors">
                  <Icon size={22} />
                </div>
                <h3 className="text-slate-900 font-semibold text-lg mb-2 tracking-tight">{w.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{w.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Approach */}
        <div className="mt-16 pt-14 border-t border-slate-100">
          <div className="text-[#0A2540] uppercase text-xs font-bold tracking-[0.25em] mb-6 text-center">Our 4-step approach</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {approachSteps.map((a) => (
              <div key={a.step} className="p-6 rounded-xl bg-slate-50 border border-slate-100">
                <div className="text-3xl font-extrabold text-[#0A2540] mb-2 tabular-nums">{a.step}</div>
                <div className="text-slate-900 font-semibold mb-1">{a.title}</div>
                <div className="text-slate-600 text-sm leading-relaxed">{a.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// -------- CUSTOMIZED SOLUTIONS --------
function CustomizedSolutions() {
  const pillars = [
    { title: 'Tailored scope', desc: 'Every proposal is built to your site — hours, headcount, equipment and reporting cadence.' },
    { title: 'Flexible bundles', desc: 'Pick one service line or combine several under a single SLA with unified escalation.' },
    { title: 'Scale on demand', desc: 'Ramp up for events, project handovers and seasonal peaks in days, not weeks.' },
    { title: 'Sector SOPs', desc: 'Purpose-built standard operating procedures for hospitals, IT, manufacturing, retail and hospitality.' },
  ];
  return (
    <section className="py-16 md:py-36 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-14 items-center">
        <div>
          <div className="text-[#0A2540] uppercase text-xs font-bold tracking-[0.25em] mb-4">Customized Solutions</div>
          <h2 className="text-3xl md:text-4xl lg:text-[46px] font-bold text-slate-900 leading-[1.05] tracking-tight">
            Customized solutions for every business.
          </h2>
          <p className="text-slate-600 mt-5 text-[15px] leading-relaxed max-w-xl">
            No two facilities are alike. We start with a free walkthrough of your premises, benchmark your current operations and design a bespoke facility programme aligned to your standards, budget and KPIs.
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {pillars.map((p) => (
              <div key={p.title} className="p-5 rounded-2xl bg-slate-50 border border-slate-100 hover:border-slate-200 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-[#F5B800] text-[#0A2540] flex items-center justify-center mb-3">
                  <BadgeCheck size={18} />
                </div>
                <div className="font-semibold text-slate-900 text-[15px]">{p.title}</div>
                <div className="text-xs text-slate-600 mt-1 leading-relaxed">{p.desc}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-cta inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-[14px]">
              Build my solution <ArrowRight size={16} />
            </Link>
            <a href={`https://wa.me/${brand.whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-slate-800 hover:text-[#0A2540] px-6 py-3 rounded-lg font-semibold text-[14px] transition-colors border border-slate-200 hover:border-slate-300">
              <MessageCircle size={16} /> WhatsApp us
            </a>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-2xl overflow-hidden shadow-md aspect-[4/5]">
            <img src="/assets/mechanised-housekeeping.jpg" alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
          </div>
          <div className="rounded-2xl overflow-hidden shadow-md aspect-[4/5] mt-8">
            <img src="/assets/hk-lobby.webp" alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
          </div>
          <div className="rounded-2xl overflow-hidden shadow-md aspect-[4/5] -mt-8">
            <img src="/assets/fire-training.webp" alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
          </div>
          <div className="rounded-2xl overflow-hidden shadow-md aspect-[4/5] bg-slate-50">
            <img src="/assets/front-office.png" alt="Durga front-office & reception team" className="w-full h-full object-contain hover:scale-105 transition-transform duration-700" />
          </div>
        </div>
      </div>
    </section>
  );
}


// -------- INDUSTRIES --------
function Industries() {
  return (
    <section id="industries" className="py-16 md:py-36 bg-slate-50 border-y border-slate-100">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="text-[#0A2540] uppercase text-xs font-bold tracking-[0.25em] mb-4">Industries we serve</div>
            <h2 className="text-3xl md:text-4xl lg:text-[46px] font-bold text-slate-900 leading-[1.05] tracking-tight">
              Facilities that never sleep. Standards that never slip.
            </h2>
          </div>
          <div className="text-slate-500 text-sm max-w-sm">
            Sector-specific SOPs, uniforms and safety protocols tailored to each industry we serve.
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {industries.map((ind) => {
            const Icon = iconMap[ind.icon] || Building2;
            return (
              <div
                key={ind.name}
                className="group relative rounded-2xl overflow-hidden aspect-[4/5] cursor-pointer bg-slate-900 ring-1 ring-slate-200 hover:ring-2 hover:ring-[#F5B800] hover:shadow-2xl transition-all duration-500"
              >
                <img
                  src={ind.image}
                  alt={ind.name}
                  loading="lazy"
                  style={{ objectPosition: ind.imgPos || 'center' }}
                  className="absolute inset-0 w-full h-full object-cover saturate-[1.05] contrast-[1.02] brightness-[0.98] group-hover:scale-[1.06] group-hover:saturate-[1.15] transition-all duration-700 ease-out"
                />
                {/* subtle top-to-bottom + slight radial vignette for premium feel */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/45 to-slate-950/10" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-t from-[#0A2540]/60 via-transparent to-transparent transition-opacity duration-500" />
                <div className="absolute inset-0 p-5 flex flex-col justify-end text-white">
                  <div className="w-10 h-10 rounded-lg bg-white text-[#0A2540] flex items-center justify-center mb-3 shadow-md group-hover:bg-[#F5B800] group-hover:scale-110 transition-all duration-300">
                    <Icon size={18} />
                  </div>
                  <div className="font-semibold leading-tight text-[15px] drop-shadow-md">{ind.name}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// -------- CERTIFICATIONS --------
function Certifications() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-[1300px] mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
          <div>
            <div className="text-[#0A2540] uppercase text-xs font-bold tracking-[0.25em] mb-2">Certifications &amp; compliance</div>
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Accredited, audited and always compliant.</h3>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {certifications.map((c) => (
            <div key={c.name} className="bg-slate-50 rounded-xl p-5 border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all">
              <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 text-[#0A2540] flex items-center justify-center mb-3">
                <BadgeCheck size={18} />
              </div>
              <div className="font-bold text-slate-900 text-sm">{c.name}</div>
              <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">{c.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------- TESTIMONIALS --------
function Testimonials() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % testimonials.length), 7000);
    return () => clearInterval(t);
  }, []);
  const t = testimonials[idx];

  return (
    <section className="py-16 md:py-36 bg-slate-50 border-y border-slate-100">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-[#0A2540] uppercase text-xs font-bold tracking-[0.25em] mb-4">Client stories</div>
          <h2 className="text-3xl md:text-4xl lg:text-[46px] font-bold text-slate-900 leading-[1.05] tracking-tight">
            Trusted by leaders across India.
          </h2>
        </div>

        <div className="relative bg-white rounded-3xl border border-slate-100 p-8 md:p-14 overflow-hidden">
          <Quote className="absolute top-8 right-8 text-slate-100" size={80} />

          <div key={t.id} className="relative animate-fadeUp">
            <div className="flex items-center gap-1 mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} className="fill-[#F5B800] text-[#F5B800]" />
              ))}
            </div>
            <p className="text-xl md:text-2xl leading-relaxed text-slate-800 font-medium max-w-4xl">
              &ldquo;{t.text}&rdquo;
            </p>

            <div className="mt-8 flex items-center gap-4 pt-8 border-t border-slate-100">
              <div className="w-14 h-14 rounded-xl bg-white overflow-hidden border border-slate-200 shrink-0">
                <img src={t.logo} alt={t.name} className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="font-bold text-slate-900">{t.name}</div>
                <div className="text-sm text-slate-500">{t.role} · {t.location}</div>
              </div>
              <div className="flex-1" />
              <div className="flex items-center gap-2">
                <button onClick={() => setIdx((i) => (i - 1 + testimonials.length) % testimonials.length)} className="w-10 h-10 rounded-full border border-slate-200 hover:border-[#F5B800] hover:bg-[#F5B800] hover:text-[#0A2540] text-slate-700 flex items-center justify-center transition-colors" aria-label="Previous">
                  <ChevronLeft size={16} />
                </button>
                <button onClick={() => setIdx((i) => (i + 1) % testimonials.length)} className="w-10 h-10 rounded-full border border-slate-200 hover:border-[#F5B800] hover:bg-[#F5B800] hover:text-[#0A2540] text-slate-700 flex items-center justify-center transition-colors" aria-label="Next">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-center gap-1.5">
          {testimonials.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} className={`h-1 rounded-full transition-all ${i === idx ? 'w-10 bg-[#F5B800]' : 'w-5 bg-slate-300 hover:bg-slate-400'}`} aria-label={`Testimonial ${i + 1}`} />
          ))}
        </div>
      </div>
    </section>
  );
}

// -------- CLIENTS MARQUEE --------
function ClientsMarquee() {
  const row1 = clientLogos.slice(0, 15);
  const row2 = clientLogos.slice(15);
  const track1 = [...row1, ...row1];
  const track2 = [...row2, ...row2];

  return (
    <section id="clients" className="py-20 bg-white">
      <div className="max-w-[1300px] mx-auto px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="text-[#0A2540] uppercase text-xs font-bold tracking-[0.25em] mb-3">Our clients</div>
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 leading-tight tracking-tight">
            103+ organisations trust Durga every single day.
          </h3>
        </div>
      </div>

      <div className="marquee-wrap space-y-5 overflow-hidden">
        <div className="marquee-track flex gap-5 w-max">
          {track1.map((logo, i) => (
            <div key={i} className="w-36 h-20 bg-slate-50 rounded-xl p-3 flex items-center justify-center border border-slate-100 shrink-0">
              <img src={logo} alt="" className="max-w-full max-h-full object-contain grayscale hover:grayscale-0 opacity-75 hover:opacity-100 transition-all" />
            </div>
          ))}
        </div>
        <div className="marquee-track flex gap-5 w-max" style={{ animationDirection: 'reverse', animationDuration: '55s' }}>
          {track2.map((logo, i) => (
            <div key={i} className="w-36 h-20 bg-slate-50 rounded-xl p-3 flex items-center justify-center border border-slate-100 shrink-0">
              <img src={logo} alt="" className="max-w-full max-h-full object-contain grayscale hover:grayscale-0 opacity-75 hover:opacity-100 transition-all" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------- OFFICES STRIP --------
function OfficesStrip() {
  const offices = [
    { ...contactInfo.registered, tag: 'HQ · Mangalore' },
    { ...contactInfo.bangalore, tag: 'Bengaluru' },
    { ...contactInfo.mumbai, tag: 'Mumbai' },
  ];
  return (
    <section id="offices" className="py-14 md:py-28 bg-white border-t border-slate-100">
      <div className="max-w-[1300px] mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-[#0A2540] uppercase text-xs font-bold tracking-[0.25em] mb-4">Our Offices</div>
          <h2 className="text-3xl md:text-4xl lg:text-[46px] font-bold text-slate-900 leading-[1.05] tracking-tight">
            Reach us at any of our branches.
          </h2>
          <p className="text-slate-600 mt-4 text-[15px]">Regional teams closer to your facility, ready to mobilise across Karnataka, Goa, Maharashtra, Tamil Nadu and Telangana.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {offices.map((o, i) => {
            const hasMap = !!o.mapUrl;
            const openMap = () => window.open(o.mapUrl, '_blank', 'noopener,noreferrer');
            const onKeyDown = (e) => {
              if (hasMap && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                openMap();
              }
            };
            return (
              <div
                key={i}
                onClick={hasMap ? openMap : undefined}
                onKeyDown={onKeyDown}
                role={hasMap ? 'link' : undefined}
                tabIndex={hasMap ? 0 : undefined}
                data-testid={hasMap ? `office-card-${o.tag.toLowerCase().replace(/[^a-z0-9]+/g, '-')}` : undefined}
                className={`group p-7 rounded-2xl bg-slate-50 border border-slate-100 hover:border-slate-200 hover:shadow-lg transition-all ${hasMap ? 'cursor-pointer hover:border-[#F5B800]/40' : ''}`}
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#F5B800] bg-[#F5B800]/10 border border-[#F5B800]/30 rounded-full px-3 py-1">
                    <MapPin size={11} /> {o.tag}
                  </div>
                  {hasMap && (
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 group-hover:text-[#0A2540] transition-colors">
                      View on map →
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3 tracking-tight">{o.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">{o.address}</p>
                <div className="space-y-2 text-sm">
                  {o.phone && (
                    <a
                      href={`tel:${o.phone}`}
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-2 text-slate-800 hover:text-[#0A2540] transition-colors font-semibold w-fit"
                    >
                      <Phone size={14} className="text-[#F5B800]" /> {o.phone}
                    </a>
                  )}
                  {o.email && (
                    <a
                      href={`mailto:${o.email}`}
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-2 text-slate-600 hover:text-[#0A2540] transition-colors w-fit underline decoration-dotted decoration-slate-400 underline-offset-4"
                    >
                      <BadgeCheck size={14} className="text-[#F5B800]" /> {o.email}
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}


// -------- CTA --------
function CTA() {
  return (
    <section className="py-20 md:py-24 bg-slate-50 border-t border-slate-100">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
        <div className="relative rounded-3xl bg-white border border-slate-100 p-10 md:p-16 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#0A2540] bg-[#F5B800]/20 px-3 py-1 rounded-full border border-[#F5B800]/40 mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F5B800]" /> Free on-site audit
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-[46px] font-bold text-slate-900 leading-[1.05] tracking-tight">
                Ready to elevate your facility operations?
              </h2>
              <p className="text-slate-600 text-base md:text-lg mt-5 max-w-xl">
                Book a free walkthrough with our senior consultants. Get a benchmarked report and a tailored proposal within 72 hours &mdash; no obligation.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Link to="/contact" className="btn-cta inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl font-semibold">
                Request a Quote <ArrowRight size={16} />
              </Link>
              <a href={`https://wa.me/${brand.whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] text-white px-7 py-4 rounded-xl font-semibold transition-colors">
                <MessageCircle size={16} /> Chat on WhatsApp
              </a>
              <a href={`tel:${brand.phone}`} className="inline-flex items-center justify-center gap-2 text-slate-800 hover:text-[#0A2540] px-7 py-4 rounded-xl font-semibold transition-colors border border-slate-200 hover:border-slate-300 bg-white">
                <Phone size={16} /> {brand.phone}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// -------- HOME --------
export default function Home() {
  return (
    <div>
      <Hero />
      <StatsStrip />
      <AboutSnippet />
      <ServicesGrid />
      <DroneVideoStrip />
      <CustomizedSolutions />
      <WhyDurgaHome />
      <Industries />
      <Certifications />
      <Testimonials />
      <ClientsMarquee />
      <FAQ />
      <CTA />
    </div>
  );
}
